import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { App as CapacitorApp } from '@capacitor/app';

import RoisBackgroundLocation from '../plugins/roisBackgroundLocation';
import { isNativeApp } from '../utils/positionSource';
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds, ModalStateType } from '../components/modals/SuperModal/types';
import { getCurrentModal } from '../state/selectors/ui/modal';
import {
  isSystemPromptOpen,
  onSystemPromptClosed,
  onSystemPromptOpened,
} from '../utils/systemPrompts';
import {
  ROUTE_START_POLL_MS,
  ROUTES_UNKNOWN_MAX_WAIT_MS,
} from './usePhotoPermissionsGate';

/**
 * Vigila el permiso de ubicación en SEGUNDO PLANO y, cuando falta, abre el
 * modal bloqueante que manda al operario a Ajustes.
 *
 * ─── Qué protege exactamente ───────────────────────────────────────────────
 *
 * NO la captura. Mientras el foreground service de tipo `location` corre,
 * Android no considera que la app esté en segundo plano, así que la traza sigue
 * aunque el operario apague la pantalla, atienda una llamada o cambie de app.
 *
 * Protege la RECUPERACIÓN, que son dos caminos reales en una tablet que pasa
 * ocho horas en un coche:
 *   - el watchdog recreando el servicio si el sistema lo mata, y
 *   - el boot receiver reanudando tras reiniciar la tablet.
 * Sin el permiso, la traza se corta EN SILENCIO y nadie se entera hasta que
 * alguien la revisa.
 *
 * ─── 🔴 Por qué NO basta con mirar si está concedido ───────────────────────
 *
 * Porque en una instalación recién hecha NADA está concedido, y la versión
 * anterior tapaba la app nada más entrar: el operario veía una pantalla
 * exigiéndole un permiso que la app no le había pedido jamás.
 *
 * Los permisos de primer plano se piden dentro de `start()`, o sea al iniciar
 * un tramo. Hasta ese momento no ha habido ni un diálogo. Y Android no ayuda a
 * distinguirlo: en Ajustes, un permiso que nunca se ha pedido aparece igual de
 * «No permitido» que uno rechazado a conciencia.
 *
 * Quien sí lo distingue es `checkPermissions()`, que devuelve `'prompt'`
 * mientras no se haya preguntado. Así que la condición para exigir el permiso
 * de segundo plano es que el de PRIMER plano ya esté concedido: eso solo puede
 * pasar si el operario pasó por el diálogo. Antes de eso el modal no aparece.
 *
 * ─── Por qué no se puede resolver con un diálogo ───────────────────────────
 *
 * Desde Android 11 el diálogo del sistema ya no ofrece la opción de permitir
 * siempre: hay que llevar al usuario a Ajustes. Por eso el modal tiene un botón
 * que abre la ficha de la app y no un `requestPermissions()`.
 *
 * ─── Por qué se re-comprueba al volver ─────────────────────────────────────
 *
 * Android no emite ningún evento al conceder un permiso. El operario sale a
 * Ajustes, toca la opción y vuelve: ese `resume` es la señal de que algo pudo
 * cambiar, y es cuando se vuelve a mirar para cerrar el modal solo.
 *
 * ─── 🔴 Espera su turno (una sola ranura de modal) ─────────────────────────
 *
 * `openModal` REEMPLAZA el modal que hubiera. Antes este guardián abría el suyo
 * sin mirar, y con el de permisos de fotos (`usePhotoPermissionsGate`) los dos
 * se disputaban la pantalla al volver del diálogo de ubicación, en orden
 * aleatorio (spec fotos geolocalizadas, § 15.2). Ahora, como los demás
 * guardianes, espera a que la ranura quede libre y entonces vuelve a mirar.
 */
/** Lo que el TimerOverlay sabe del recorrido de esta tablet. */
export interface BackgroundLocationGateRoute {
  /** Esta tablet graba un recorrido: su GPS pide lo suyo primero. */
  routeCapturing?: boolean;
  /** Ya se sabe qué recorridos hay activos. */
  routesKnown?: boolean;
}

export function useBackgroundLocationGate(
  enabled: boolean,
  { routeCapturing = false, routesKnown = true }: BackgroundLocationGateRoute = {}
): void {
  const dispatch = useDispatch();
  // 🔴 Primero el recorrido, como el aviso de fotos. Visto en la W el
  // 25-09-2026: con la ubicación ya concedida este aviso se abría antes de que
  // el GPS del recorrido pidiera las notificaciones; al salir su diálogo se
  // apartaba y volvía — asomaba y se iba. Mientras no se sabe si esta tablet
  // graba (con tope), o mientras su GPS no graba, se espera.
  const routeCapturingRef = useRef(routeCapturing);
  routeCapturingRef.current = routeCapturing;
  const routesKnownRef = useRef(routesKnown);
  routesKnownRef.current = routesKnown;
  const mountedAt = useRef(Date.now());
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Solo el id del modal en pantalla: este hook vive en el `TimerOverlay`, en
  // el Header de toda la app.
  const currentModalId = useSelector(
    (state: unknown) => (getCurrentModal(state) as ModalStateType | undefined)?.id
  );
  const visibleModalId = useRef(currentModalId);
  visibleModalId.current = currentModalId;
  // Ref además de la prop: el listener de `resume` se crea una vez y leería un
  // valor congelado.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Quién abrió el modal. Sin esto, cerrarlo al conceder el permiso cerraría
  // también cualquier otro modal que el operario tuviera abierto.
  const abiertoPorMi = useRef(false);

  const cerrar = useCallback(() => {
    if (!abiertoPorMi.current) return;
    abiertoPorMi.current = false;
    dispatch(closeModal());
  }, [dispatch]);

  // `check` se reprograma a sí mismo; la ref rompe el ciclo.
  const checkRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const check = useCallback(async () => {
    if (retry.current) {
      clearTimeout(retry.current);
      retry.current = null;
    }
    if (!isNativeApp() || !enabledRef.current) {
      cerrar();
      return;
    }
    try {
      // 🔴 Primero: ¿se le ha llegado a preguntar? Si el permiso de primer
      // plano no está concedido, o no ha habido diálogo todavía o el operario
      // lo denegó — y en ninguno de los dos casos tiene sentido exigirle el de
      // segundo plano. Cuando inicie un tramo, `start()` preguntará.
      const permisos = await RoisBackgroundLocation.checkPermissions();
      if (permisos.location !== 'granted') {
        cerrar();
        return;
      }

      const status = await RoisBackgroundLocation.checkBackgroundPermission();
      if (status.granted) {
        cerrar();
        return;
      }
      if (abiertoPorMi.current) return;
      if (!routesKnownRef.current) {
        const waited = Date.now() - mountedAt.current;
        if (waited < ROUTES_UNKNOWN_MAX_WAIT_MS) {
          retry.current = setTimeout(
            () => void checkRef.current(),
            ROUTES_UNKNOWN_MAX_WAIT_MS - waited
          );
          return;
        }
      }
      if (routeCapturingRef.current) {
        let running = true;
        try {
          running = (await RoisBackgroundLocation.isRunning()).running;
        } catch {
          // Una APK que no sabe decirlo no deja esperando para siempre.
          running = true;
        }
        if (!running) {
          retry.current = setTimeout(
            () => void checkRef.current(),
            ROUTE_START_POLL_MS
          );
          return;
        }
      }
      // Nunca por encima de otro modal: se espera a que quede libre (efecto
      // de abajo). Ni con la hoja de Google «¿Guardar contraseña?» encima
      // (ver `systemPrompts`): al cerrarse se vuelve a mirar.
      if (visibleModalId.current) return;
      if (isSystemPromptOpen()) return;
      abiertoPorMi.current = true;
      dispatch(
        openModal(modalIds.BACKGROUND_LOCATION_REQUIRED, {
          optionLabel: status.optionLabel,
          onOpenSettings: () => {
            void RoisBackgroundLocation.openAppSettings().catch(() => {
              // Si ni siquiera se pueden abrir los ajustes no hay nada que
              // ofrecer: el modal sigue y el operario tendrá que llegar a mano.
            });
          },
        })
      );
    } catch {
      // Un APK viejo sin los métodos nuevos, o un fallo del puente: no se
      // bloquea. Impedir trabajar por no poder comprobar sería peor que el
      // problema que esto previene.
      cerrar();
    }
  }, [cerrar, dispatch]);
  checkRef.current = check;

  useEffect(() => {
    void check();
  }, [check, enabled, routeCapturing, routesKnown]);

  useEffect(
    () => () => {
      if (retry.current) clearTimeout(retry.current);
    },
    []
  );

  // La ranura cambió. Si el nuestro ya no está, otro lo reemplazó o lo cerró:
  // deja de ser nuestro. Y si queda libre, es el turno de volver a mirar.
  useEffect(() => {
    if (
      abiertoPorMi.current &&
      currentModalId !== modalIds.BACKGROUND_LOCATION_REQUIRED
    ) {
      abiertoPorMi.current = false;
    }
    if (!currentModalId) void check();
  }, [currentModalId, check]);

  useEffect(() => {
    if (!isNativeApp()) return;
    let handle: { remove: () => Promise<void> } | null = null;
    let cancelled = false;
    void CapacitorApp.addListener('resume', () => void check()).then((h) => {
      if (cancelled) {
        void h.remove();
        return;
      }
      handle = h;
    });
    return () => {
      cancelled = true;
      if (handle) void handle.remove();
    };
  }, [check]);

  useEffect(() => onSystemPromptClosed(() => void check()), [check]);

  // Si algo del sistema sale encima del nuestro, el nuestro se retira y vuelve
  // al mirar de nuevo (ver `onSystemPromptOpened`). Medido en la W: el GPS del
  // recorrido pidió las notificaciones 0,1 s después de abrirse este aviso.
  useEffect(() => onSystemPromptOpened(cerrar), [cerrar]);

  // Si el componente que lo usa se desmonta, el modal no debe quedarse solo en
  // pantalla sin nadie que lo cierre.
  useEffect(() => cerrar, [cerrar]);
}

export default useBackgroundLocationGate;
